import {
  type PropsWithChildren,
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
} from 'react';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import { useApolloClient } from '@apollo/client';
import { useDelayedState } from '@frameio/vapor-migration';
import { useAssetTransferState } from '../../shared';
import { useAssetJobsStatusFragment } from '../../shared/gql/generated/AssetJobsStatus';
import { useTranscodeProgressFragment } from '../../shared/gql/generated/TranscodeProgress';
import { useTranscriptionEncodeStatusFragment } from '../../shared/gql/generated/TranscriptionEncodeStatus';
import type { AssetState } from '../types';
import {
  getStateForAssetJob,
  getStateForFirefly,
  getStateForInteractive,
  getStateForTranscribing,
  getStateForTransfers,
} from '../utils';

export type UploadProgress = {
  /** The current transfer rate */
  bytesPerSecond: number;
  /** Is the upload paused? */
  isPaused: boolean;
  /** Percentage completion of the upload */
  percentage: number;
  /** Estimated time remaining */
  remainingTime: number;
};

type AssetStateContextValue = {
  assetState: AssetState;
  onPulseIteration: () => void;
  uploadProgress: UploadProgress;
};

const AssetStateContext = createContext<AssetStateContextValue>({
  assetState: 'complete',
  onPulseIteration: noop,
  uploadProgress: {
    percentage: 0,
    remainingTime: 0,
    isPaused: false,
    bytesPerSecond: 0,
  },
});

type Props = PropsWithChildren<{
  id: string;
}>;

export function AssetStateContextProvider({ children, id }: Props) {
  const { cache } = useApolloClient();

  const from = useMemo(
    () =>
      cache.identify({
        __typename: 'Asset',
        id,
      }) ?? '',
    [cache, id]
  );

  const transcodesFragment = useTranscodeProgressFragment(id);
  const transcriptionData = useTranscriptionEncodeStatusFragment(id);
  const assetJobsFragment = useAssetJobsStatusFragment(id);
  const transferState = useAssetTransferState(from);

  /**
   * Derive the current assetState based on the result of a series of function
   * calls. Idea here is to add a new function for each set of cases that can
   * impact the state of the asset. Please note that assets can only be in one
   * state at a time. Also be aware that order matters here, as the function should
   * exit early as soon as a state function returns a value that isn't undefined.
   *
   * Another very important thing to point out is that getStateForTransfers should
   * always be last as it is the only function that won't return undefined and has
   * the final say on the state of the asset.
   */
  const assetState = useMemo(() => {
    const firefly = getStateForFirefly(transcodesFragment);
    if (firefly) return firefly;

    const assetJob = getStateForAssetJob(assetJobsFragment);
    if (assetJob) return assetJob;

    const transcribing = getStateForTranscribing(transcriptionData);
    if (transcribing) return transcribing;

    const interactive = getStateForInteractive(transcodesFragment);
    if (interactive) return interactive;

    return getStateForTransfers(transferState);
  }, [transcodesFragment, assetJobsFragment, transcriptionData, transferState]);

  const uploadProgress = useMemo<UploadProgress>(
    () => ({
      bytesPerSecond: transferState?.progress?.bytesPerSecond ?? 0,
      isPaused: transferState?.isPaused ?? false,
      percentage: transferState?.progress?.percentage ?? 0,
      remainingTime: transferState?.progress?.remainingTime ?? 0,
    }),
    [
      transferState?.progress?.bytesPerSecond,
      transferState?.isPaused,
      transferState?.progress?.percentage,
      transferState?.progress?.remainingTime,
    ]
  );

  /**
   * This logic handles sequencing the transition from "pulsing" to "pulsing-thumbnail"
   * or "complete" status. We delay updating the status until various conditions
   * are met.
   *
   * 1. "pulsing" has occurred for at least 2 seconds
   * 2. The pulse iteration has completed, and is at its smallest size.
   */
  const [isPulsing, setIsPulsing] = useState(false);

  /**
   * 1. Delay the reveal of the thumbnail. This should ensure
   * that the thumbnail has loaded and rendered, while also working
   * for assets that don't render thumbnails.
   */
  const delayedStatus = useDelayedState(assetState, {
    delay: COMPLETE_STATE_DELAY,
    initialState: assetState,
    enabled: assetState === 'complete',
  });

  /**
   *  2. When the pulse iteration occurs, check if our status is no longer
   * "generating-thumbnail" and also not "firefly-generating". If it is, then we can stop pulsing.
   */

  const onPulseIteration = useCallback(() => {
    if (
      assetState !== 'generating-thumbnail' &&
      assetState !== 'firefly-generating' &&
      assetState !== 'remote'
    ) {
      setIsPulsing(false);
    }
  }, [assetState]);

  const isVisuallyGeneratingThumbnail =
    assetState === 'generating-thumbnail' ||
    delayedStatus === 'generating-thumbnail' ||
    isPulsing;

  const visualStatus = isVisuallyGeneratingThumbnail
    ? 'generating-thumbnail'
    : assetState;

  const context = useMemo<AssetStateContextValue>(() => {
    return {
      assetState: visualStatus,
      uploadProgress,
      onPulseIteration,
    };
  }, [onPulseIteration, uploadProgress, visualStatus]);

  const deferredContext = useDeferredValue(context);

  return useMemo(() => {
    return (
      <AssetStateContext.Provider value={deferredContext}>
        {children}
      </AssetStateContext.Provider>
    );
  }, [deferredContext, children]);
}

const COMPLETE_STATE_DELAY = 1250;

export const useOnPulseIteration = () =>
  useContextSelector(AssetStateContext, (context) => context.onPulseIteration);

export const useUploadProgress = () =>
  useContextSelector(AssetStateContext, (context) => context.uploadProgress);

export const useAssetState = () =>
  useContextSelector(AssetStateContext, (context) => context.assetState);
